import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import StatTile from '@components/Common/StatTile';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { canWriteJobsAndCompanies, isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import dayjs from '@helpers/date';
import ActionIconButton from '@components/Common/ActionIconButton';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, Button, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRememberListView } from '@helpers/listView';
import './index.scss';
import AddSourceForm from './AddSourceForm';
import ClicksPanel from './ClicksPanel';
import {
  API,
  canApproveSource,
  canRunSource,
  canScoutSource,
  errorMessage,
  useSourceActions,
} from './sourceActions';
import { formatDate, formatDateTime } from '@helpers/date';

/*
 * External Jobs: the sources mintly-external-jobs-service reads from employers' own careers sites,
 * their runs, the alerts they raise and the daily summary. Alerts are shown here and nowhere else
 * (owner decision, 2026-10-06: nothing is pushed), so acknowledging one here is what clears it.
 *
 * Everything goes through the BFF at /api/external-jobs, which holds the service's key and records
 * the signed-in admin as the actor. HR and Super Admins see the page; changing a source's status
 * and a takedown are Super Admin only, here and again in the BFF.
 */

const PAGE_SIZE = 50;

export { canApproveSource, canRunSource, errorMessage };

export interface Source {
  sourceId: string;
  companyName?: string;
  brandNames?: string[];
  adapter?: string;
  careersUrl?: string;
  status: string;
  activeJobs?: number;
  /** Postings the last run fetched, before country filtering and close-out. */
  lastJobCount?: number | null;
  lastRunAt?: string | null;
  lastRunStatus?: string | null;
  declineReason?: string | null;
  reviewReason?: string | null;
  scout?: { outcome?: string | null; error?: string | null } | null;
}

/** What the Scout is doing for a source, in words, or null once it has nothing to say. */
export const scoutLine = (s: Pick<Source, 'status' | 'scout'>): string | null => {
  const outcome = s.scout?.outcome;
  if (!outcome || s.status !== 'NEEDS_REVIEW') return null;
  return (
    {
      queued: 'Scout queued',
      running: 'Scout is reading the site',
      proposed: 'Scout found the jobs; check the first fetch',
      blocked: 'Scout was blocked by the site',
      robots: 'The site asks robots not to read it',
      no_url: 'No careers page to read',
      error: 'Scout failed',
      unverified: 'Scout found possible job data but could not read jobs from it',
      no_job_feed_found: 'Scout found no job listings or job feed on the careers page',
      unreachable: 'Scout could not reach the site',
      robots_disallowed: 'The site asks robots not to read its careers page',
      feed_robots_disallowed: 'The job feed exists but the site asks robots not to read it',
      page_failed: 'The careers page would not load in the Scout',
      llm_not_configured: 'Scout found job data but had no AI to read it',
    } as Record<string, string>
  )[outcome] ?? `Scout: ${outcome}`;
};

export interface Run {
  runId: string;
  sourceId: string;
  trigger?: string;
  status: string;
  startedAt?: string;
  durationMs?: number;
  counts?: Record<string, number>;
  /** Requests the run made, by kind: `{ list: 27, detail: 269 }`. An object, never a number. */
  requests?: Record<string, number> | null;
  errors?: ({ kind?: string; message?: string } | string)[];
}

/** Total requests a run made. The service records them by kind, so this sums whatever is there. */
export const requestTotal = (requests?: Record<string, number> | number | null): number =>
  typeof requests === 'number'
    ? requests
    : Object.values(requests ?? {}).reduce((n, v) => n + (typeof v === 'number' ? v : 0), 0);

/** A run error is recorded as `{kind, message}`; anything else is shown as text, never rendered raw. */
export const runError = (e: unknown): string =>
  typeof e === 'string' ? e : e && typeof e === 'object' ? String((e as any).message ?? (e as any).kind ?? '') : '';

export interface Alert {
  alertId: string;
  kind: string;
  severity?: string;
  message: string;
  context?: { sourceId?: string };
  createdAt?: string;
}

export interface Summary {
  date: string;
  sourcesRun: number;
  sourcesFailed: number;
  jobsNew: number;
  jobsClosed: number;
  activeJobs: number;
  llmCostUsd: number;
  alertsRaised: number;
  alertsOpen: number;
  sourcesNeedingReview?: string[];
}

const TABS = ['sources', 'runs', 'alerts', 'clicks', 'summary'] as const;
type TabKey = (typeof TABS)[number];
const TAB_LABELS: Record<TabKey, string> = {
  sources: 'Sources',
  runs: 'Runs',
  alerts: 'Alerts',
  clicks: 'Clicks',
  summary: 'Daily summary',
};

const STATUS_FILTER = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'NEEDS_REVIEW', label: 'Needs review' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'DECLINED', label: 'Declined' },
];

const when = (v?: string | null) => formatDateTime(v, '');
/** AI spend is cents a day, so it keeps two decimals; fmtMoney rounds to whole units. */
export const usd = (v?: number) => `$${(v ?? 0).toFixed(2)}`;

/** "12 new, 3 changed, 1 closed of 40 fetched": the run's counts as the service records them. */
export const runCounts = (counts: Record<string, number> = {}): string => {
  const parts = [
    counts.new ? `${counts.new} new` : '',
    counts.changed ? `${counts.changed} changed` : '',
    counts.closed ? `${counts.closed} closed` : '',
  ].filter(Boolean);
  const fetched = counts.fetched ?? 0;
  return `${parts.length ? parts.join(', ') : 'No changes'} of ${fetched} fetched`;
};

/**
 * Whether the row offers "Review jobs": it has jobs to show, or it is waiting on review and has
 * run, in which case the jobs tab is where to look before approving, even when the run found none.
 */
export const hasJobsToReview = (s: Source): boolean =>
  (s.activeJobs ?? 0) > 0 || (s.lastJobCount ?? 0) > 0 || (s.status === 'NEEDS_REVIEW' && !!s.lastRunAt);

const ExternalJobs: React.FC = () => {
  const role = useCurrentUserRole();
  const superAdmin = isSuperAdmin(role);
  // Marketing reads this page; running, scouting and adding sources put jobs on the site (owner's brief, 2026-10-19).
  const canOperate = canWriteJobsAndCompanies(role);
  const { showToast } = useToast();
  const navigate = useNavigate();
  useRememberListView('/external-jobs');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabKey = (TABS as readonly string[]).includes(searchParams.get('tab') ?? '')
    ? (searchParams.get('tab') as TabKey)
    : 'sources';

  const [sources, setSources] = useState<Source[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summaries, setSummaries] = useState<{ today: Summary | null; past: Summary[] }>({ today: null, past: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* In the query string, so Back from a source's workspace returns to the same filtered page. */
  const statusFilter = searchParams.get('filter_status') ?? '';
  const page = Math.max(parseInt(searchParams.get('page') ?? '0', 10) || 0, 0);
  const setSourcesView = (status: string, nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (status) next.set('filter_status', status);
    else next.delete('filter_status');
    if (nextPage > 0) next.set('page', String(nextPage));
    else next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const [runSource, setRunSource] = useState('');
  const [runs, setRuns] = useState<Run[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([axios.get(`${API}/sources`), axios.get(`${API}/alerts`), axios.get(`${API}/summaries`)])
      .then(([s, a, d]) => {
        setSources(s.data ?? []);
        setAlerts(a.data ?? []);
        setSummaries({ today: d.data?.today ?? null, past: d.data?.past ?? [] });
      })
      .catch((e) => setError(errorMessage(e, 'External jobs could not be loaded.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);
  const actions = useSourceActions(load);
  /** A source is a record: its details, fetched jobs and runs open as its own workspace. */
  const openSource = (s: Source, tab?: 'jobs') =>
    navigate(`/external-jobs/${encodeURIComponent(s.sourceId)}${tab ? `/${tab}` : ''}`);

  useEffect(() => {
    if (tab !== 'runs') return;
    setRunsLoading(true);
    axios
      .get(`${API}/runs`, { params: { sourceId: runSource || undefined, limit: 100 } })
      .then((r) => setRuns(r.data ?? []))
      .catch((e) => showToast(errorMessage(e, 'Runs could not be loaded.'), 'error'))
      .finally(() => setRunsLoading(false));
  }, [tab, runSource, showToast]);

  const setTab = (i: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', TABS[i]);
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(
    () => (statusFilter ? sources.filter((s) => s.status === statusFilter) : sources),
    [sources, statusFilter],
  );
  const liveJobs = sources.reduce((n, s) => n + (s.activeJobs ?? 0), 0);
  const sourceName = useMemo(() => new Map(sources.map((s) => [s.sourceId, s.companyName || s.sourceId])), [sources]);
  const activeSources = sources.filter((s) => s.status === 'ACTIVE').length;

  const acknowledge = async (a: Alert) => {
    try {
      await axios.post(`${API}/alerts/${a.alertId}/ack`, {});
      setAlerts((list) => list.filter((x) => x.alertId !== a.alertId));
      showToast('Alert acknowledged', 'success');
    } catch (e) {
      showToast(errorMessage(e, 'The alert was not acknowledged.'), 'error');
    }
  };

  const kpis = (
    <Box className="cc-stat-grid">
      <StatTile label="Live jobs" value={liveJobs.toLocaleString('en-IN')} icon={<WorkOutlineOutlinedIcon />} />
      <StatTile label="Active sources" value={`${activeSources} of ${sources.length}`} icon={<SourceOutlinedIcon />} />
      <StatTile
        label="Open alerts"
        value={String(alerts.length)}
        icon={<NotificationsActiveOutlinedIcon />}
        tone={alerts.length ? 'danger' : 'success'}
      />
      <StatTile label="AI spend today" value={usd(summaries.today?.llmCostUsd)} icon={<PaymentsOutlinedIcon />} />
    </Box>
  );

  const sourcesPanel = (
    <>
      <FilterRow>
        <SelectFormField
          required={false}
          name="sourceStatus"
          label="Status"
          value={statusFilter}
          options={STATUS_FILTER}
          onChange={(_n, v) => setSourcesView(String(v), 0)}
        />
      </FilterRow>
      {filtered.length === 0 ? (
      <EmptyState compact title="No sources match" body="Try another status." />
    ) : (
      <MintlyTable
        pagination={{
          enabled: filtered.length > PAGE_SIZE,
          page,
          rowsPerPage: PAGE_SIZE,
          totalNumberOfRows: filtered.length,
          handleChangePage: (_e, n) => setSourcesView(statusFilter, n),
        }}
        rows={filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)}
        selectors={['source', 'status', 'jobs', 'lastRun', 'actions']}
        /* The whole row opens the source's workspace (status, fetch history, fetched jobs). The
           careers-site link and the action buttons stop propagation below so they keep their own
           behaviour instead of also opening the record. */
        onRowClick={(s: Source) => openSource(s)}
        tableHeadRow={{ source: <>Source</>, status: <>Status</>, jobs: <>Live jobs</>, lastRun: <>Last run</>, actions: <></> }}
        tableBodyRow={{
          source: (s: Source) => (
            <Box className="ej-source">
              {s.careersUrl ? (
                <a
                  href={s.careersUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ej-source-name"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s.companyName || s.sourceId}
                </a>
              ) : (
                <span className="ej-source-name">{s.companyName || s.sourceId}</span>
              )}
              <span className="ej-mono">
                {s.sourceId} · {s.adapter}
              </span>
            </Box>
          ),
          status: (s: Source) => (
            <Box className="ej-source">
              <CRMStatusChip status={s.status} />
              {scoutLine(s) && (
                <Typography component="span" variant="body2" className="ej-mono">
                  {scoutLine(s)}
                </Typography>
              )}
              {(s.reviewReason || s.declineReason) && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  className="ej-reason"
                  title={s.reviewReason || s.declineReason || undefined}
                >
                  {s.reviewReason || s.declineReason}
                </Typography>
              )}
            </Box>
          ),
          // Worded, not a bare number: on a phone the cell stands alone at the top of the card.
          jobs: (s: Source) => <>{`${(s.activeJobs ?? 0).toLocaleString('en-IN')} live`}</>,
          lastRun: (s: Source) =>
            s.lastRunAt ? (
              <Box className="ej-source">
                {s.lastRunStatus && <CRMStatusChip status={s.lastRunStatus} />}
                <span>{dayjs(s.lastRunAt).fromNow()}</span>
              </Box>
            ) : (
              <>Never run</>
            ),
          actions: (s: Source) => (
            <Box className="ej-icon-actions" onClick={(e) => e.stopPropagation()}>
              {hasJobsToReview(s) && (
                <ActionIconButton
                  size="small"
                  label="Review jobs"
                  icon={<ListAltOutlinedIcon />}
                  onClick={() => openSource(s, 'jobs')}
                />
              )}
              {canOperate && canRunSource(s) && (
                <ActionIconButton size="small" label="Run now" icon={<PlayArrowOutlinedIcon />} onClick={() => actions.queueRun(s)} />
              )}
              {canOperate && canScoutSource(s) && (
                <ActionIconButton size="small" label="Scout the site again" icon={<TravelExploreOutlinedIcon />} onClick={() => actions.scoutAgain(s)} />
              )}
              {superAdmin && canApproveSource(s) && (
                <ActionIconButton
                  size="small"
                  label="Approve: show its jobs on the site"
                  color="primary"
                  icon={<CheckCircleOutlinedIcon />}
                  onClick={() => actions.approve(s)}
                />
              )}
              {superAdmin && s.status === 'ACTIVE' && (
                <ActionIconButton
                  size="small"
                  label="Pause daily runs"
                  icon={<PauseCircleOutlinedIcon />}
                  onClick={() => actions.pause(s)}
                />
              )}
              {superAdmin && s.status !== 'DECLINED' && (
                <ActionIconButton
                  size="small"
                  label="Take down: remove its jobs now"
                  color="error"
                  icon={<DeleteForeverOutlinedIcon />}
                  onClick={() => actions.takedown(s)}
                />
              )}
            </Box>
          ),
        }}
      />
      )}
    </>
  );

  const runsPanel = (
    <>
      <FilterRow>
        <SelectFormField
          required={false}
          name="runSource"
          label="Source"
          value={runSource}
          options={[{ value: '', label: 'All sources' }, ...sources.map((s) => ({ value: s.sourceId, label: s.companyName || s.sourceId }))]}
          onChange={(_n, v) => setRunSource(String(v))}
        />
      </FilterRow>
      {runsLoading && runs.length === 0 ? (
        <SkeletonLoader rows={6} columns={5} />
      ) : runs.length === 0 ? (
        <EmptyState compact title="No runs yet" />
      ) : (
        <MintlyTable
          pagination={{ enabled: false, page: 0, rowsPerPage: runs.length, totalNumberOfRows: runs.length, handleChangePage: () => {} }}
          rows={runs}
          selectors={['started', 'source', 'status', 'counts', 'requests']}
          tableHeadRow={{ started: <>Started</>, source: <>Source</>, status: <>Status</>, counts: <>Result</>, requests: <>Requests</> }}
          tableBodyRow={{
            started: (r: Run) => (
              <Box className="ej-source">
                <span>{when(r.startedAt)}</span>
                <span className="ej-mono">
                  {r.trigger}
                  {r.durationMs ? `, ${Math.round(r.durationMs / 1000)}s` : ''}
                </span>
              </Box>
            ),
            source: (r: Run) => (
              <Box className="ej-source">
                <span className="ej-source-name">{sourceName.get(r.sourceId) ?? r.sourceId}</span>
                <span className="ej-mono">{r.sourceId}</span>
              </Box>
            ),
            status: (r: Run) => (
              <Box className="ej-source">
                <CRMStatusChip status={r.status} />
                {runError(r.errors?.[0]) && (
                  <Typography component="span" variant="body2" color="text.secondary" className="ej-wrap">
                    {runError(r.errors?.[0])}
                  </Typography>
                )}
              </Box>
            ),
            counts: (r: Run) => <>{runCounts(r.counts)}</>,
            // AI spend is not per run: enrichment is billed in its own ledger, see Daily summary.
            requests: (r: Run) => <>{requestTotal(r.requests).toLocaleString('en-IN')}</>,
          }}
        />
      )}
    </>
  );

  const alertsPanel =
    alerts.length === 0 ? (
      <EmptyState compact title="No open alerts" body="Blocked or broken sources, the close-out guard and OpenAI errors show up here." />
    ) : (
      <MintlyTable
        pagination={{ enabled: false, page: 0, rowsPerPage: alerts.length, totalNumberOfRows: alerts.length, handleChangePage: () => {} }}
        rows={alerts}
        selectors={['raised', 'kind', 'message', 'actions']}
        tableHeadRow={{ raised: <>Raised</>, kind: <>Kind</>, message: <>What happened</>, actions: <></> }}
        tableBodyRow={{
          raised: (a: Alert) => <>{when(a.createdAt)}</>,
          kind: (a: Alert) => (
            <Box className="ej-source">
              <span className="ej-mono">{a.kind}</span>
              {a.context?.sourceId && <span className="ej-mono">{a.context.sourceId}</span>}
            </Box>
          ),
          message: (a: Alert) => <span className="ej-wrap">{a.message}</span>,
          actions: (a: Alert) => (
            <Box className="ej-icon-actions">
              {canOperate && (
                <ActionIconButton size="small" label="Acknowledge" icon={<DoneAllOutlinedIcon />} onClick={() => acknowledge(a)} />
              )}
            </Box>
          ),
        }}
      />
    );

  const days = [
    ...(summaries.today ? [{ ...summaries.today, date: `${formatDate(summaries.today.date)} (so far)` }] : []),
    ...summaries.past.map((d) => ({ ...d, date: formatDate(d.date) })),
  ];
  const summaryPanel =
    days.length === 0 ? (
      <EmptyState compact title="No summaries yet" body="One is written for each day just after midnight UTC." />
    ) : (
      <MintlyTable
        pagination={{ enabled: false, page: 0, rowsPerPage: days.length, totalNumberOfRows: days.length, handleChangePage: () => {} }}
        rows={days}
        selectors={['date', 'runs', 'jobs', 'live', 'alerts', 'cost']}
        tableHeadRow={{ date: <>Day (UTC)</>, runs: <>Sources run</>, jobs: <>New and closed</>, live: <>Live jobs</>, alerts: <>Alerts</>, cost: <>AI spend</> }}
        tableBodyRow={{
          date: (d: Summary) => <>{d.date}</>,
          runs: (d: Summary) => <>{`${d.sourcesRun}${d.sourcesFailed ? `, ${d.sourcesFailed} failed` : ''}`}</>,
          jobs: (d: Summary) => <>{`${d.jobsNew} new, ${d.jobsClosed} closed`}</>,
          live: (d: Summary) => <>{d.activeJobs.toLocaleString('en-IN')}</>,
          alerts: (d: Summary) => <>{`${d.alertsRaised} raised, ${d.alertsOpen} open`}</>,
          cost: (d: Summary) => <>{usd(d.llmCostUsd)}</>,
        }}
      />
    );

  return (
    <CRMPageTemplate
      icon={<PublicOutlinedIcon />}
      title="External Jobs"
      subtitle="Jobs read from employers' own careers sites, shown on the site alongside Mintly's own."
      onAddNew={canOperate ? () => {
        setAdding(true);
        setTab(TABS.indexOf('sources'));
      } : undefined}
      addNewLabel="Add careers link"
      /* Header, then tabs, then content: the tabs used to sit under the stat tiles, the one page
         where they were not directly below the header. */
      tabs={{
        value: TABS.indexOf(tab),
        onChange: setTab,
        tabs: TABS.map((t) => ({ label: t === 'alerts' && alerts.length ? `Alerts (${alerts.length})` : TAB_LABELS[t] })),
      }}
    >
      <Box className="ej-page">
        {error ? (
          <EmptyState
            compact
            title="External jobs could not be loaded"
            body={error}
            actions={
              <Button variant="outlined" onClick={load}>
                Try again
              </Button>
            }
          />
        ) : loading ? (
          <SkeletonLoader rows={8} columns={5} />
        ) : (
          <>
            {kpis}
            {tab === 'sources' && adding && (
              <AddSourceForm
                onAdded={() => {
                  setAdding(false);
                  load();
                }}
                onCancel={() => setAdding(false)}
              />
            )}
            {tab === 'sources' && sourcesPanel}
            {tab === 'runs' && runsPanel}
            {tab === 'alerts' && alertsPanel}
            {tab === 'clicks' && (
              <ClicksPanel sources={sources.map((x) => ({ value: x.sourceId, label: x.companyName || x.sourceId }))} />
            )}
            {tab === 'summary' && summaryPanel}
          </>
        )}
      </Box>

      {actions.dialog}
    </CRMPageTemplate>
  );
};

export default ExternalJobs;
